import { CdkTreeModule, FlatTreeControl } from '@angular/cdk/tree';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Input,
  OnInit,
  Output,
  TemplateRef,
  TrackByFunction,
  untracked,
  viewChild,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { TranslocoPipe } from '@jsverse/transloco';
import { Observable, of, take } from 'rxjs';
import { isTruthy } from 'shared/util-common';

import {
  AddNodeOption,
  ElementDroppedEvent,
  FlatTreeNode,
  GuidelineChapterType,
  NodeAddedEvent,
  NodeDroppedEvent,
  TableOfContentNodeType,
  TreeNode,
  TreeNodeTemplateContext,
} from '@treetop/shared/model';

import { TreeDragDropIndicatorComponent } from './tree-drag-drop/drop-indicator/tree-drag-drop-indicator.component';
import { TreeDragDropDirective } from './tree-drag-drop/tree-drag-drop.directive';
import { TreeDragDropInstruction, TreeDragDropInstructionType, TreeDragDropService } from './tree-drag-drop/tree-drag-drop.service';
import { TreeComponentService } from './tree.component.service';
import { shouldRender } from './tree.util';
import { ButtonDirective } from '../button/button.directive';
import { IconDirective } from '../icon/icon.directive';
import { InputDirective, InputSuffixDirective } from '../input';
import { MenuItemDirective } from '../menu/menu-item.directive';
import { MenuTriggerDirective } from '../menu/menu-trigger.directive';
import { MenuComponent } from '../menu/menu.component';
import { PopoverRef, PopoverService } from '../popover';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';

export let TT_TREE_DRAG_DROP_ID = 0;

@Component({
  standalone: true,
  selector: 'tt-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  imports: [
    CdkTreeModule,
    MatIconModule,
    IconDirective,
    TooltipDirective,
    InputDirective,
    NgTemplateOutlet,
    FormsModule,
    InputSuffixDirective,
    TranslocoPipe,
    MatTreeModule,
    TreeDragDropIndicatorComponent,
    TreeDragDropDirective,
    MenuComponent,
    MenuItemDirective,
    MenuTriggerDirective,
    SpinnerComponent,
    ButtonDirective,
    JsonPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TreeComponentService, TreeDragDropService],
})
export class TreeComponent<T extends TreeNode<unknown>> implements OnInit {
  protected service = inject(TreeComponentService);
  protected dragService = inject(TreeDragDropService);
  protected popoverService = inject(PopoverService);
  private viewContainerRef = inject(ViewContainerRef);

  protected treeControl = new FlatTreeControl<FlatTreeNode<T>>(
    (node) => node.level,
    (node) => node.expandable,
  );

  @HostBinding('class.tt-tree--readonly') public get isReadonly() {
    return this._readonly;
  }

  protected hasChild = (_: number, node: FlatTreeNode<T>) => node.expandable;

  @Input() public title: string;
  @Input() public showExpandToggle = true;
  @Input() public showAllButton = true;
  @Input() public showAddButton = true;
  @Input() public activeNodeId: string;
  @Input() public showDot = true;
  @Input() public addNodeOptions: AddNodeOption<unknown>[] = [];
  @Input() public maxDepth = Infinity;

  /**
   * Optional template used to render the content of each node (e.g. label, badges,
   * checkboxes). When provided it replaces the default title rendering. The template
   * receives a {@link TreeNodeTemplateContext} so consumers can read the node data,
   * its selection state and its level. Node selection is still handled by the tree.
   */
  public nodeTemplate = input<TemplateRef<TreeNodeTemplateContext<T>> | undefined>(undefined);

  /**
   * @property waitForDataChange {boolean}
   * If true, the tree will display a loading spinner and disable interaction
   * until new data is provided. This is useful when each change triggers a backend call—
   * the edit event is emitted, but the tree remains blocked until the updated value
   * is received from the parent component.
   */
  @Input() public waitForDataChange = false;

  public expandedNodes = input<T[]>([]);

  public expandAll = input(false);

  @Input() public set allowMultipleSelection(allowMultipleSelection: boolean) {
    this.service.allowMultipleSelection = allowMultipleSelection;
  }

  @Input() public set disableChildrenOnSelection(disableChildrenOnSelection: boolean) {
    this.service.disableChildrenOnSelection = disableChildrenOnSelection;
  }

  protected _readonly = false;
  @Input() public set readonly(readonly: boolean) {
    this.cancelEditNode();
    this._readonly = readonly;
  }

  public selected = input<T[]>([]);
  public disabled = input<T[]>([]);
  public dataSource = input.required<T[]>();
  public nodeItemTemplate = input<TemplateRef<unknown>>();

  @Input() public beforeDeleteCallback: (node: T) => Observable<boolean> = () => of(true);

  @Output() public nodeAdded = new EventEmitter<NodeAddedEvent<unknown>>();
  @Output() public nodeDeleted = new EventEmitter<T>();
  @Output() public nodeEdited = new EventEmitter<T>();
  @Output() public nodesSelected = new EventEmitter<T[]>();
  @Output() public elementDropped = new EventEmitter<ElementDroppedEvent>();
  @Output() public nodeDropped = new EventEmitter<NodeDroppedEvent>();

  protected newlyAddedNode: T | undefined;
  protected currentlyEditedNode: T | undefined;
  private newlyAddedNodeOption: unknown;
  protected temporaryName = '';

  private addNewNodePopoverRef: PopoverRef;

  protected readonly ttTreeId = TT_TREE_DRAG_DROP_ID++;

  @ViewChild('editNodeInput') public editNodeInput: ElementRef<HTMLInputElement>;
  public addNewNodeMenu = viewChild.required<TemplateRef<unknown>>('addNewNodeMenu');

  constructor() {
    effect(() => {
      this.service.selectNodes(this.selected());
    });

    effect(() => {
      this.service.disableNodes(this.disabled());
    });

    effect(() => {
      const dataSource = this.service.vm().dataSource;

      untracked(() => {
        this.treeControl.dataNodes = dataSource;
      });
    });

    effect(() => {
      const dataSource = this.dataSource();

      untracked(() => {
        this.service.setDataSource(dataSource);
      });
    });
  }

  public ngOnInit() {
    this.service.init({
      data: this.dataSource(),
      selected: this.selected(),
      expanded: this.expandedNodes(),
      expandAll: this.expandAll(),
    });
  }

  protected toggleExpandNode(node: FlatTreeNode<T>, event: MouseEvent) {
    event.stopPropagation();

    if (this.currentlyEditedNode) {
      return;
    }

    this.service.toggleExpandNode(node.node);
  }

  protected selectAndEmitNodes(node?: FlatTreeNode<T>) {
    if (node?.disabled) {
      return;
    }

    this.service.selectNode(node?.node);

    this.nodesSelected.emit(this.service.selected());
  }

  protected addNode(node?: FlatTreeNode<T>, addNodeOption?: unknown) {
    if (this.currentlyEditedNode) {
      return;
    }

    this.newlyAddedNode = this.service.addNode(node);
    this.newlyAddedNodeOption = addNodeOption;

    this.currentlyEditedNode = this.newlyAddedNode;
    this.temporaryName = this.newlyAddedNode.title;
  }

  protected editNode(node: FlatTreeNode<T>, event?: MouseEvent) {
    event?.stopPropagation();

    if (this.currentlyEditedNode) {
      return;
    }

    this.currentlyEditedNode = node.node;
    this.temporaryName = node.node.title;
  }

  protected removeNode(node: FlatTreeNode<T>) {
    if (this.currentlyEditedNode) {
      return;
    }

    this.beforeDeleteCallback(node.node)
      .pipe(take(1), isTruthy())
      .subscribe(() => {
        this.service.removeNode(node.node);
        this.nodeDeleted.emit(node.node);
        this.nodesSelected.emit(this.service.selected());

        if (this.waitForDataChange) {
          this.service.waitForData.set(true);
        }
      });
  }

  protected finalizeEditedNode() {
    this.currentlyEditedNode.title = this.temporaryName;

    this.service.updateNode(this.currentlyEditedNode);

    if (this.newlyAddedNode) {
      this.nodeAdded.emit({ node: this.currentlyEditedNode, addNodeOption: this.newlyAddedNodeOption });
    } else {
      this.nodeEdited.emit(this.currentlyEditedNode);
    }

    if (this.waitForDataChange) {
      this.service.waitForData.set(true);
    }

    this.currentlyEditedNode = undefined;
    this.newlyAddedNode = undefined;
    this.newlyAddedNodeOption = undefined;
    this.temporaryName = '';
  }

  protected cancelEditNode() {
    this.currentlyEditedNode = undefined;
    this.temporaryName = '';

    if (this.newlyAddedNode) {
      this.service.removeNode(this.newlyAddedNode);
      this.newlyAddedNode = undefined;
      this.newlyAddedNodeOption = undefined;
    }
  }

  protected handleInput($event: KeyboardEvent) {
    if ($event.key === 'Enter' && this.temporaryName.trim() !== '') {
      this.finalizeEditedNode();
      return;
    }

    if ($event.key === 'Escape') {
      this.cancelEditNode();
      return;
    }
  }

  protected onTreeNodeDropped(node: FlatTreeNode<T>, target: FlatTreeNode<T>, instruction: TreeDragDropInstruction) {
    if (instruction.type === TreeDragDropInstructionType.Blocked) {
      return;
    }

    if (node.node.id === target.node.id) {
      return;
    }

    const editedNode = this.service.onDragDrop(node.node, target.node, instruction);

    this.nodeDropped.emit({
      node: node.node,
      oldParentChapterId: node.node.parentId,
      newParentChapterId: editedNode.parentId,
      index: editedNode.order,
    });

    this.service.toggleExpandNode(target, 'open');
    if (this.waitForDataChange) {
      this.service.waitForData.set(true);
    }
  }

  protected onElementDropped(data: string, node: FlatTreeNode<T>) {
    this.elementDropped.emit({ source: data, target: node.node });
  }

  protected handleNewNodeButtonClick(node?: FlatTreeNode<T>, $event?: MouseEvent) {
    if (!this.addNodeOptions?.length) return this.addNode(node);

    this.addNewNodePopoverRef = this.popoverService.open(
      this.addNewNodeMenu(),
      $event.target as HTMLElement,
      {
        offsetY: 16,
        positionStrategy: 'bottom-right',
      },
      this.viewContainerRef,
    );

    this.addNewNodePopoverRef.afterClosed().subscribe((result: unknown) => {
      if (result !== undefined) {
        this.addNode(node, result);
      }
    });
  }

  protected trackByFn: TrackByFunction<FlatTreeNode<T>> = (_, item) => {
    return `${item.node.id}-${item.node.title}-${item.node.parentId}-${item.expandable}-${item.selected}-${item.node.no}-${item.isExpanded}`;
  };

  protected readonly shouldRender = shouldRender;
  protected readonly GuidelineChapterType = GuidelineChapterType;
  protected readonly TableOfContentNodeType = TableOfContentNodeType;
}
